import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  Building2, Shield, TrendingUp, FileText, Calculator, Download, Printer,
  ChevronRight, CheckCircle, Clock, DollarSign, Percent, Calendar,
  ArrowLeft, RefreshCw, Info, Lock, Scale, BadgeCheck, AlertTriangle,
  BookOpen, FileCheck, Landmark, CreditCard, Settings, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BETH_TOKEN = '0x6822824c5349bf005e353ec5dda3fa34595ea811';

// Treasury-Indexed Private Obligation Products - Renamed
const TIPO_PRODUCTS = [
  {
    id: 'TISTO-30',
    name: 'Treasury-Indexed Short-Term Obligation',
    shortName: 'TISTO-30',
    category: 'Short-Term',
    term: 30,
    referenceRate: 'U.S. Treasury 1-Month CMR',
    spread: 0.25,
    baseYield: 4.25,
    minInvestment: 100000,
    maxInvestment: 10000000,
    description: 'Short-term private obligation indexed to U.S. Treasury 1-Month Constant Maturity Rate.',
    features: ['30-Day Term', 'BETH Credit Enhancement', 'UCC-1 Secured', 'Tokenized Settlement']
  },
  {
    id: 'TIMTN-90',
    name: 'Treasury-Indexed Medium-Term Note',
    shortName: 'TIMTN-90',
    category: 'Medium-Term',
    term: 90,
    referenceRate: 'U.S. Treasury 3-Month CMR',
    spread: 0.50,
    baseYield: 5.50,
    minInvestment: 250000,
    maxInvestment: 25000000,
    description: 'Medium-term private note indexed to U.S. Treasury 3-Month Constant Maturity Rate.',
    features: ['90-Day Term', 'BETH Credit Enhancement', 'UCC-1 Secured', 'Quarterly Reset']
  },
  {
    id: 'TIMTN-180',
    name: 'Treasury-Indexed Medium-Term Note',
    shortName: 'TIMTN-180',
    category: 'Medium-Term',
    term: 180,
    referenceRate: 'U.S. Treasury 6-Month CMR',
    spread: 0.75,
    baseYield: 6.75,
    minInvestment: 500000,
    maxInvestment: 50000000,
    description: 'Medium-term private note indexed to U.S. Treasury 6-Month Constant Maturity Rate.',
    features: ['180-Day Term', 'BETH Credit Enhancement', 'UCC-1 Secured', 'Priority Settlement']
  },
  {
    id: 'TILTO-365',
    name: 'Treasury-Indexed Long-Term Obligation',
    shortName: 'TILTO-365',
    category: 'Long-Term',
    term: 365,
    referenceRate: 'U.S. Treasury 1-Year CMR',
    spread: 1.00,
    baseYield: 8.00,
    minInvestment: 1000000,
    maxInvestment: 100000000,
    description: 'Long-term private obligation indexed to U.S. Treasury 1-Year Constant Maturity Rate.',
    features: ['365-Day Term', '125% Collateralization', 'UCC-1 Secured', 'Roll-Forward Option']
  }
];

// Platform Fee Structure
const FEE_STRUCTURE = {
  platformAccess: {
    title: 'Platform Access Fees',
    description: 'Users (trusts / SPVs / accredited entities) pay for access to the platform. Not tied to any specific transaction.',
    tiers: [
      { name: 'Basic Access', price: 2500, period: 'monthly', features: ['Platform Dashboard', 'Basic Reporting', 'Email Support'] },
      { name: 'Treasury-Indexed', price: 7500, period: 'monthly', features: ['All Basic features', 'Treasury Index Engine', 'Priority Support', 'API Access'] },
      { name: 'Credit-Enhanced', price: 15000, period: 'monthly', features: ['All Treasury features', 'Credit Enhancement Facility', 'Dedicated Account Manager', 'Custom Integrations'] },
      { name: 'Enterprise License', price: 150000, period: 'annual', features: ['Unlimited Access', 'White-Label Options', 'On-Premise Deployment', 'SLA Guarantee'] }
    ],
    safe: true,
    reason: 'Not transaction-based, not capital-based, not performance-based'
  },
  documentation: {
    title: 'Documentation & Issuance Fees',
    description: 'Fees for drafting/generating obligations, issuing credit enhancement agreements, administering PAS agreements, creating tokenized settlement instructions.',
    fees: [
      { name: 'Treasury-Indexed Obligation Issuance', amount: 5000, type: 'per-issuance' },
      { name: 'Credit Enhancement Agreement', amount: 3500, type: 'per-document' },
      { name: 'PAS Agreement Execution', amount: 2500, type: 'per-document' },
      { name: 'UCC-1 Financing Statement', amount: 1500, type: 'per-filing' },
      { name: 'Tokenized Settlement Instruction', amount: 500, type: 'per-instruction' },
      { name: 'Document Package (Complete Bundle)', amount: 10000, type: 'per-package' }
    ],
    safe: true,
    reason: 'Legal, defensible, standard in private credit markets'
  },
  administration: {
    title: 'Administration & Servicing Fees',
    description: 'Ongoing administration including ledger maintenance, index calculation, reset processing, reporting, wallet controls, compliance review.',
    fees: [
      { name: 'Annual Servicing Fee', amount: 5000, type: 'annual-flat' },
      { name: 'Monthly Admin Fee', amount: 500, type: 'monthly-flat' },
      { name: 'Quarterly Reset Processing', amount: 250, type: 'per-reset' },
      { name: 'Compliance Review', amount: 1000, type: 'per-review' },
      { name: 'Custom Reporting', amount: 750, type: 'per-report' }
    ],
    safe: true,
    reason: 'Fixed or capped, not % of principal'
  },
  creditEnhancement: {
    title: 'Credit Enhancement Facility Fees',
    description: 'Fees for credit-enhancement layer. Think of this like a letter-of-credit availability fee, not a guarantee premium.',
    fees: [
      { name: 'Facility Setup Fee', amount: 10000, type: 'one-time' },
      { name: 'Enhancement Availability Fee', amount: 2500, type: 'monthly' },
      { name: 'Standby Support Fee', amount: 5000, type: 'annual' },
      { name: 'Credit Enhancement Activation', amount: 7500, type: 'per-activation' }
    ],
    safe: true,
    reason: 'NOT interest, NOT yield, NOT success-based'
  },
  technology: {
    title: 'Technology & Treasury Services Fees',
    description: 'Fees for Treasury index engine access, settlement instruction tooling, wallet tagging, treasury analytics. Positions platform as technology provider and treasury services administrator.',
    fees: [
      { name: 'Treasury Index Engine Access', amount: 5000, type: 'monthly' },
      { name: 'Settlement Instruction Tooling', amount: 2000, type: 'monthly' },
      { name: 'Wallet Tagging Service', amount: 1000, type: 'monthly' },
      { name: 'Treasury Analytics Dashboard', amount: 3000, type: 'monthly' },
      { name: 'API Integration Setup', amount: 15000, type: 'one-time' }
    ],
    safe: true,
    reason: 'Technology provider, treasury services administrator - NOT a financial intermediary'
  }
};

const TreasurySecurities = () => {
  const { user, token } = useAuth();
  const { account, bethBalance } = useWeb3();
  const navigate = useNavigate();
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [costSheet, setCostSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('products');
  const [showClientDetailsModal, setShowClientDetailsModal] = useState(false);
  
  // Client details form
  const [clientDetails, setClientDetails] = useState({
    client_name: '',
    client_entity_type: 'individual',
    client_address: '',
    client_city: '',
    client_state: '',
    client_zip: '',
    client_email: '',
    client_phone: '',
    client_tax_id: ''
  });

  useEffect(() => {
    if (user) {
      setClientDetails(prev => ({
        ...prev,
        client_name: user.name || '',
        client_email: user.email || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      fetchOrders();
    }
  }, [token]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/treasury-securities/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const calculateCostSheet = (product, amount) => {
    const principal = parseFloat(amount) || 0;
    if (principal < product.minInvestment) return null;
    
    const annualYield = product.baseYield / 100;
    const termYield = annualYield * (product.term / 365);
    const grossAccrual = principal * termYield;
    
    // Platform Fees (from fee schedule)
    const issuanceFee = 5000; // Per issuance
    const adminFee = product.term <= 90 ? 500 : product.term <= 180 ? 1000 : 1500;
    const enhancementFee = 2500;
    const totalFees = issuanceFee + adminFee + enhancementFee;
    
    const netAccrual = grossAccrual - totalFees;
    const maturityValue = principal + netAccrual;
    const effectiveYield = (netAccrual / principal) * (365 / product.term) * 100;
    
    // BETH collateral requirement
    const bethRequired = principal; // At $1.00 per BETH
    const collateralRatio = product.term <= 90 ? 1.0 : product.term <= 180 ? 1.1 : 1.25;
    const bethCollateral = bethRequired * collateralRatio;
    
    return {
      principal,
      term: product.term,
      category: product.category,
      referenceRate: product.referenceRate,
      spread: product.spread,
      baseYield: product.baseYield,
      grossAccrual: grossAccrual.toFixed(2),
      issuanceFee: issuanceFee.toFixed(2),
      adminFee: adminFee.toFixed(2),
      enhancementFee: enhancementFee.toFixed(2),
      totalFees: totalFees.toFixed(2),
      netAccrual: netAccrual.toFixed(2),
      maturityValue: maturityValue.toFixed(2),
      effectiveYield: effectiveYield.toFixed(2),
      bethCollateral: bethCollateral.toFixed(0),
      collateralRatio: (collateralRatio * 100).toFixed(0),
      maturityDate: new Date(Date.now() + product.term * 24 * 60 * 60 * 1000).toLocaleDateString()
    };
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setInvestmentAmount(product.minInvestment.toString());
    setCostSheet(calculateCostSheet(product, product.minInvestment));
  };

  const handleAmountChange = (value) => {
    setInvestmentAmount(value);
    if (selectedProduct) {
      setCostSheet(calculateCostSheet(selectedProduct, value));
    }
  };

  const handleCreateOrder = async () => {
    if (!selectedProduct || !costSheet) {
      toast.error('Please select a product and enter investment amount');
      return;
    }
    
    if (!token) {
      toast.error('Please log in to create an order');
      return;
    }
    
    // Show client details modal if not filled
    if (!clientDetails.client_name || !clientDetails.client_address) {
      setShowClientDetailsModal(true);
      return;
    }
    
    setLoading(true);
    try {
      const fullAddress = `${clientDetails.client_address}, ${clientDetails.client_city}, ${clientDetails.client_state} ${clientDetails.client_zip}`;
      
      const res = await fetch(`${API}/treasury-securities/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          principal: parseFloat(investmentAmount),
          term_days: selectedProduct.term,
          base_yield: selectedProduct.baseYield,
          reference_rate: selectedProduct.referenceRate,
          spread: selectedProduct.spread,
          cost_sheet: costSheet,
          // Client details
          client_name: clientDetails.client_name,
          client_entity_type: clientDetails.client_entity_type,
          client_address: fullAddress,
          client_email: clientDetails.client_email,
          client_phone: clientDetails.client_phone,
          client_tax_id: clientDetails.client_tax_id
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(`Order ${data.order.order_id} created successfully!`);
        setActiveTab('orders');
        fetchOrders();
        setTimeout(() => printTIPODocument(data.order), 500);
      } else {
        toast.error(data.error || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating order');
    } finally {
      setLoading(false);
    }
  };

  const printTIPODocument = (order) => {
    const printContent = generateTIPODocument(order || { ...selectedProduct, cost_sheet: costSheet, order_id: 'PREVIEW' });
    const printWindow = window.open('', '_blank', 'width=900,height=800,scrollbars=yes,resizable=yes');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 500);
      toast.success('Document opened for printing');
    } else {
      toast.error('Please allow popups to print');
    }
  };

  const printFeeSchedule = () => {
    const printContent = generateFeeScheduleDocument();
    const printWindow = window.open('', '_blank', 'width=900,height=800,scrollbars=yes,resizable=yes');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 500);
      toast.success('Fee Schedule opened for printing');
    } else {
      toast.error('Please allow popups to print');
    }
  };

  const generateFeeScheduleDocument = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bethereum Platform Fee Schedule</title>
        <style>
          body { font-family: 'Georgia', serif; padding: 40px; max-width: 900px; margin: 0 auto; line-height: 1.6; color: #0a1628; }
          .document-container { position: relative; padding: 40px; border: 3px double #0a1628; background: white; }
          .watermark { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.04; font-size: 100px; font-weight: bold; color: #0a1628; pointer-events: none; }
          .header { text-align: center; border-bottom: 3px double #b8860b; padding-bottom: 25px; margin-bottom: 30px; }
          .header h1 { color: #0a1628; font-size: 28px; margin: 0; letter-spacing: 4px; }
          .header .subtitle { color: #b8860b; font-size: 11px; letter-spacing: 2px; margin-top: 5px; }
          .section { margin: 25px 0; padding: 20px; border: 1px solid #ddd; background: #fafafa; }
          .section-title { font-size: 14px; color: #b8860b; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px; border-bottom: 2px solid #b8860b; padding-bottom: 8px; font-weight: bold; }
          .fee-table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          .fee-table th, .fee-table td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
          .fee-table th { background: #0a1628; color: white; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; }
          .fee-table td { font-size: 11px; }
          .fee-amount { font-weight: bold; color: #b8860b; }
          .safe-badge { display: inline-block; background: #d1fae5; color: #065f46; padding: 3px 8px; border-radius: 3px; font-size: 9px; margin-left: 10px; }
          .disclaimer { background: #fef3c7; border: 2px solid #f59e0b; padding: 15px; margin: 25px 0; font-size: 10px; }
          .prohibited { background: #fee2e2; border: 2px solid #dc2626; padding: 15px; margin: 25px 0; }
          .prohibited h4 { color: #dc2626; margin: 0 0 10px 0; }
          .prohibited ul { margin: 0; padding-left: 20px; font-size: 11px; }
          .payment-stack { background: linear-gradient(135deg, #f8f4e8 0%, #fefefe 100%); border: 2px solid #b8860b; padding: 20px; margin: 25px 0; }
          .payment-stack h4 { color: #0a1628; margin: 0 0 15px 0; }
          .stack-item { display: flex; align-items: center; padding: 10px 0; border-bottom: 1px dashed #b8860b; }
          .stack-item:last-child { border-bottom: none; }
          .stack-arrow { color: #b8860b; margin: 0 15px; font-size: 18px; }
          .footer { margin-top: 30px; padding-top: 20px; border-top: 2px solid #0a1628; font-size: 9px; color: #666; text-align: center; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="document-container">
          <div class="watermark">BETHEREUM</div>
          
          <div class="header">
            <h1>BETHEREUM</h1>
            <p class="subtitle">PLATFORM FEE SCHEDULE</p>
            <p style="font-size: 10px; color: #666; margin-top: 10px;">Effective Date: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="disclaimer">
            <strong>⚠️ FEE DISCLOSURE:</strong> Fees are charged for platform access, administrative services, documentation, and credit-enhancement availability. 
            <strong>No fees are contingent upon investment success, performance, or capital formation.</strong>
          </div>
          
          <div class="section">
            <div class="section-title">1. Platform Access Fees <span class="safe-badge">✓ PRIMARY & SAFEST</span></div>
            <p style="font-size: 11px; color: #666; margin-bottom: 15px;">Users (trusts / SPVs / accredited entities) pay for access to the platform. Not tied to any specific transaction.</p>
            <table class="fee-table">
              <tr><th>Tier</th><th>Price</th><th>Period</th><th>Features</th></tr>
              <tr><td>Basic Access</td><td class="fee-amount">$2,500</td><td>Monthly</td><td>Platform Dashboard, Basic Reporting, Email Support</td></tr>
              <tr><td>Treasury-Indexed</td><td class="fee-amount">$7,500</td><td>Monthly</td><td>+ Treasury Index Engine, Priority Support, API Access</td></tr>
              <tr><td>Credit-Enhanced</td><td class="fee-amount">$15,000</td><td>Monthly</td><td>+ Credit Enhancement Facility, Dedicated Account Manager</td></tr>
              <tr><td>Enterprise License</td><td class="fee-amount">$150,000</td><td>Annual</td><td>Unlimited Access, White-Label, On-Premise, SLA</td></tr>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">2. Documentation & Issuance Fees <span class="safe-badge">✓ STANDARD</span></div>
            <table class="fee-table">
              <tr><th>Service</th><th>Fee</th><th>Type</th></tr>
              <tr><td>Treasury-Indexed Obligation Issuance</td><td class="fee-amount">$5,000</td><td>Per Issuance</td></tr>
              <tr><td>Credit Enhancement Agreement</td><td class="fee-amount">$3,500</td><td>Per Document</td></tr>
              <tr><td>PAS Agreement Execution</td><td class="fee-amount">$2,500</td><td>Per Document</td></tr>
              <tr><td>UCC-1 Financing Statement</td><td class="fee-amount">$1,500</td><td>Per Filing</td></tr>
              <tr><td>Tokenized Settlement Instruction</td><td class="fee-amount">$500</td><td>Per Instruction</td></tr>
              <tr><td>Complete Document Package</td><td class="fee-amount">$10,000</td><td>Per Package</td></tr>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">3. Administration & Servicing Fees <span class="safe-badge">✓ FIXED</span></div>
            <table class="fee-table">
              <tr><th>Service</th><th>Fee</th><th>Type</th></tr>
              <tr><td>Annual Servicing Fee</td><td class="fee-amount">$5,000</td><td>Annual (Flat)</td></tr>
              <tr><td>Monthly Admin Fee</td><td class="fee-amount">$500</td><td>Monthly (Flat)</td></tr>
              <tr><td>Quarterly Reset Processing</td><td class="fee-amount">$250</td><td>Per Reset</td></tr>
              <tr><td>Compliance Review</td><td class="fee-amount">$1,000</td><td>Per Review</td></tr>
              <tr><td>Custom Reporting</td><td class="fee-amount">$750</td><td>Per Report</td></tr>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">4. Credit Enhancement Facility Fees <span class="safe-badge">✓ NOT INTEREST</span></div>
            <p style="font-size: 10px; color: #666; margin-bottom: 10px;">Like a letter-of-credit availability fee, NOT a guarantee premium.</p>
            <table class="fee-table">
              <tr><th>Service</th><th>Fee</th><th>Type</th></tr>
              <tr><td>Facility Setup Fee</td><td class="fee-amount">$10,000</td><td>One-Time</td></tr>
              <tr><td>Enhancement Availability Fee</td><td class="fee-amount">$2,500</td><td>Monthly</td></tr>
              <tr><td>Standby Support Fee</td><td class="fee-amount">$5,000</td><td>Annual</td></tr>
              <tr><td>Credit Enhancement Activation</td><td class="fee-amount">$7,500</td><td>Per Activation</td></tr>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">5. Technology & Treasury Services Fees</div>
            <table class="fee-table">
              <tr><th>Service</th><th>Fee</th><th>Type</th></tr>
              <tr><td>Treasury Index Engine Access</td><td class="fee-amount">$5,000</td><td>Monthly</td></tr>
              <tr><td>Settlement Instruction Tooling</td><td class="fee-amount">$2,000</td><td>Monthly</td></tr>
              <tr><td>Wallet Tagging Service</td><td class="fee-amount">$1,000</td><td>Monthly</td></tr>
              <tr><td>Treasury Analytics Dashboard</td><td class="fee-amount">$3,000</td><td>Monthly</td></tr>
              <tr><td>API Integration Setup</td><td class="fee-amount">$15,000</td><td>One-Time</td></tr>
            </table>
          </div>
          
          <div class="payment-stack">
            <h4>💰 PAYMENT STACK</h4>
            <div class="stack-item"><span>User / Trust / SPV</span></div>
            <div class="stack-item"><span class="stack-arrow">↓</span><span><strong>Platform Access Fee</strong> (monthly)</span></div>
            <div class="stack-item"><span class="stack-arrow">↓</span><span><strong>Issuance Fee</strong> (one-time)</span></div>
            <div class="stack-item"><span class="stack-arrow">↓</span><span><strong>Admin / Servicing Fee</strong> (fixed)</span></div>
            <div class="stack-item"><span class="stack-arrow">↓</span><span><strong>Credit Enhancement Facility Fee</strong> (fixed)</span></div>
          </div>
          
          <div class="prohibited">
            <h4>🚫 PLATFORM DOES NOT:</h4>
            <ul>
              <li>Take a % of obligation value</li>
              <li>Take a % of Treasury spread</li>
              <li>Take success fees</li>
              <li>Take referral fees</li>
              <li>Share in investor returns at the platform level</li>
              <li>Net from settlement flows</li>
            </ul>
          </div>
          
          <div style="background: #f0f9ff; border: 2px solid #0ea5e9; padding: 15px; margin: 25px 0;">
            <h4 style="color: #0369a1; margin: 0 0 10px 0;">Platform Positioning</h4>
            <p style="font-size: 11px; margin: 0;">
              Bethereum operates as: <strong>A Platform</strong> • <strong>A Technology Provider</strong> • <strong>A Document Issuer</strong> • <strong>A Credit-Enhancement Facility Administrator</strong>
              <br/><br/>
              <strong>NOT</strong> as a broker. <strong>NOT</strong> as a dealer. <strong>NOT</strong> as a yield participant (unless via a separate SPV).
            </p>
          </div>
          
          <div class="footer">
            <p>🔒 PLATFORM FEE SCHEDULE • BETHEREUM PRIVATE WEALTH MANAGEMENT</p>
            <p>BETH Token: ${BETH_TOKEN}</p>
            <p>Generated: ${new Date().toLocaleString()}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const generateTIPODocument = (order) => {
    const cs = order.cost_sheet || costSheet;
    const product = selectedProduct || TIPO_PRODUCTS.find(p => p.id === order.product_id);
    const instrumentNo = order.order_id || `TIPO-${Date.now().toString(36).toUpperCase()}`;
    const issueDate = new Date().toLocaleDateString();
    const maturityDate = cs?.maturityDate || new Date(Date.now() + (product?.term || 30) * 24 * 60 * 60 * 1000).toLocaleDateString();
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${product?.name || 'Treasury-Indexed Private Obligation'} - ${instrumentNo}</title>
        <style>
          body { font-family: 'Georgia', serif; padding: 40px; max-width: 900px; margin: 0 auto; line-height: 1.6; color: #0a1628; }
          .document-container { position: relative; padding: 40px; border: 3px double #0a1628; background: white; }
          .watermark { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.04; font-size: 100px; font-weight: bold; }
          .header { text-align: center; border-bottom: 3px double #b8860b; padding-bottom: 25px; margin-bottom: 30px; position: relative; }
          .seal { position: absolute; top: 0; left: 0; width: 70px; height: 70px; border: 2px solid #0a1628; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: white; }
          .seal span { font-size: 7px; color: #0a1628; font-weight: bold; line-height: 1.2; }
          .header h1 { color: #0a1628; font-size: 26px; margin: 0; letter-spacing: 4px; }
          .header .subtitle { color: #b8860b; font-size: 11px; letter-spacing: 2px; margin-top: 5px; font-weight: bold; }
          .instrument-number { position: absolute; top: 10px; right: 10px; font-family: 'Courier New', monospace; font-size: 10px; background: #f0f0f0; padding: 5px 10px; border: 1px solid #ccc; }
          .document-title { text-align: center; background: linear-gradient(180deg, #0a1628 0%, #1a2d4a 100%); color: white; padding: 15px; margin: 25px 0; font-size: 14px; letter-spacing: 2px; }
          .section { margin: 25px 0; padding: 20px; border: 1px solid #ddd; background: #fafafa; }
          .section-title { font-size: 12px; color: #b8860b; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px; border-bottom: 1px solid #b8860b; padding-bottom: 5px; font-weight: bold; }
          .row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px dotted #ddd; }
          .row:last-child { border-bottom: none; }
          .row .label { color: #666; }
          .row .value { font-weight: bold; color: #0a1628; }
          .highlight-box { background: linear-gradient(135deg, #f8f4e8 0%, #fefefe 100%); border: 2px solid #b8860b; padding: 20px; margin: 25px 0; text-align: center; }
          .principal-amount { font-size: 32px; font-weight: bold; color: #0a1628; }
          .legal-section { font-size: 11px; line-height: 1.8; text-align: justify; margin: 25px 0; padding: 20px; border: 1px solid #0a1628; background: white; }
          .legal-section h4 { font-size: 12px; color: #0a1628; margin: 15px 0 10px 0; text-transform: uppercase; letter-spacing: 1px; }
          .no-govt { background: #fee2e2; border: 2px solid #dc2626; padding: 15px; margin: 20px 0; text-align: center; font-size: 11px; }
          .fee-box { background: #f0f9ff; border: 2px solid #0ea5e9; padding: 15px; margin: 20px 0; }
          .fee-box h4 { color: #0369a1; margin: 0 0 10px 0; font-size: 12px; }
          .fee-row { display: flex; justify-content: space-between; font-size: 11px; padding: 5px 0; }
          .signature-section { display: flex; gap: 40px; margin-top: 50px; }
          .sig-box { flex: 1; border: 2px solid #0a1628; padding: 25px; text-align: center; background: white; }
          .sig-line { border-top: 2px solid #0a1628; width: 80%; margin: 50px auto 10px auto; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #0a1628; font-size: 9px; color: #666; text-align: center; }
          @media print { body { padding: 20px; background: white; } .document-container { border: 2px solid #000; } }
        </style>
      </head>
      <body>
        <div class="document-container">
          <div class="watermark">BETHEREUM</div>
          
          <div class="header">
            <div class="seal">
              <span>PRIVATE</span>
              <span style="font-size: 10px;">⚖</span>
              <span>OBLIGATION</span>
            </div>
            <div class="instrument-number">INSTRUMENT: ${instrumentNo}</div>
            <h1>BETHEREUM</h1>
            <p class="subtitle">PRIVATE WEALTH MANAGEMENT • ETHEREUM MAINNET</p>
          </div>
          
          <div class="document-title">${product?.name?.toUpperCase() || 'TREASURY-INDEXED PRIVATE OBLIGATION'}</div>
          
          <p style="text-align: center; font-style: italic; font-size: 12px; color: #666;">
            This is a private contractual obligation, not a security issued by the U.S. Government.
          </p>
          
          <div class="highlight-box">
            <div style="font-size: 11px; color: #666; letter-spacing: 2px; margin-bottom: 10px;">PRINCIPAL AMOUNT</div>
            <div class="principal-amount">$${parseFloat(cs?.principal || 0).toLocaleString()}</div>
            <div style="font-size: 12px; color: #b8860b; margin-top: 10px;">${product?.category || 'Treasury-Indexed'} Obligation</div>
          </div>
          
          <div class="section">
            <div class="section-title">1. Parties</div>
            <div class="row"><span class="label">Issuer / Obligor</span><span class="value">Bethereum Platform Treasury Entity</span></div>
            <div class="row"><span class="label">Holder / Participant</span><span class="value">${user?.name || 'Accredited Investor'}</span></div>
            <div class="row"><span class="label">Platform Administrator</span><span class="value">Bethereum Private Wealth Management (Administrative only; non-fiduciary)</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">2. Obligation Terms</div>
            <div class="row"><span class="label">Instrument ID</span><span class="value">${instrumentNo}</span></div>
            <div class="row"><span class="label">Category</span><span class="value">${product?.category || 'Treasury-Indexed'}</span></div>
            <div class="row"><span class="label">Issue Date</span><span class="value">${issueDate}</span></div>
            <div class="row"><span class="label">Maturity Date</span><span class="value">${maturityDate}</span></div>
            <div class="row"><span class="label">Term</span><span class="value">${cs?.term || product?.term} Days</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">3. Reference Index</div>
            <div class="row"><span class="label">Reference Rate</span><span class="value">${product?.referenceRate || 'U.S. Treasury Constant Maturity Rate'}</span></div>
            <div class="row"><span class="label">Spread Over Reference</span><span class="value">+${product?.spread || 0.50}%</span></div>
            <div class="row"><span class="label">Indexed Yield</span><span class="value" style="color: #b8860b; font-size: 16px;">${cs?.baseYield || product?.baseYield}% APY</span></div>
            <p style="font-size: 10px; color: #666; margin-top: 15px; font-style: italic;">
              Payments under this Obligation shall be indexed to the ${product?.referenceRate || 'U.S. Treasury'}, 
              as published by the Federal Reserve Economic Data (FRED), solely as a reference benchmark.
              <strong>No ownership, custody, or interest in U.S. Treasuries is created.</strong>
            </p>
          </div>
          
          <div class="fee-box">
            <h4>4. Platform Fees (Fixed, Non-Performance Based)</h4>
            <div class="fee-row"><span>Issuance Fee</span><span>$${cs?.issuanceFee || '5,000.00'}</span></div>
            <div class="fee-row"><span>Administration Fee</span><span>$${cs?.adminFee || '500.00'}</span></div>
            <div class="fee-row"><span>Credit Enhancement Fee</span><span>$${cs?.enhancementFee || '2,500.00'}</span></div>
            <div class="fee-row" style="border-top: 1px solid #0ea5e9; padding-top: 10px; margin-top: 5px;">
              <span><strong>Total Platform Fees</strong></span><span><strong>$${cs?.totalFees || '8,000.00'}</strong></span>
            </div>
            <p style="font-size: 9px; color: #666; margin-top: 10px;">
              Fees are charged for platform access, administrative services, documentation, and credit-enhancement availability. 
              No fees are contingent upon investment success, performance, or capital formation.
            </p>
          </div>
          
          <div class="section">
            <div class="section-title">5. Credit Enhancement (BETH-Backed)</div>
            <div class="row"><span class="label">BETH Collateral Required</span><span class="value">${parseFloat(cs?.bethCollateral || 0).toLocaleString()} BETH</span></div>
            <div class="row"><span class="label">Collateralization Ratio</span><span class="value">${cs?.collateralRatio || 100}%</span></div>
            <div class="row"><span class="label">Security Filing</span><span class="value">UCC-1 Financing Statement</span></div>
            <p style="font-size: 10px; color: #666; margin-top: 15px;">
              Performance of this Obligation may be supported by a separate Credit Enhancement and Participation Agreement (PAS), 
              which may reference digital assets solely as a performance support mechanism.
              <br/><strong>Important:</strong> Enhancement ≠ guarantee • Enhancement ≠ legal tender • Enhancement does not eliminate credit risk
            </p>
          </div>
          
          <div class="no-govt">
            <strong>⛔ NO GOVERNMENT AFFILIATION</strong><br/>
            This Obligation is NOT issued, guaranteed, sponsored, or insured by the United States Treasury, 
            the Federal Reserve, or any governmental authority.
          </div>
          
          <div class="legal-section">
            <h4>6. Settlement Mechanics (Tokenized Instruction)</h4>
            <p>Settlement of amounts due may be executed via a tokenized settlement instruction, functioning as a conditional payment directive subject to off-chain accounting confirmation. Tokenized Settlement Instruction: Is not money • Does not constitute legal tender • Does not effect payment absent acceptance</p>
            
            <h4>7. Transfer Restrictions</h4>
            <p>No public resale. No exchange listing. Transfers only to: (a) Accredited entities, (b) With issuer consent, (c) Ledger update required.</p>
            
            <h4>8. Platform Role Limitation</h4>
            <p>The Platform acts solely as a technology and administrative service provider and does not provide investment advice, brokerage services, or fiduciary oversight.</p>
            
            <h4>9. Governing Law</h4>
            <p>State of Delaware, USA. Private commercial contract law. UCC Articles 8 & 9 (as applicable).</p>
          </div>
          
          <div class="signature-section">
            <div class="sig-box">
              <div style="font-size: 10px; text-transform: uppercase; color: #0a1628; font-weight: bold;">Issuer / Obligor Signature</div>
              <div class="sig-line"></div>
              <div style="font-size: 10px;">Bethereum Platform Treasury</div>
              <div style="font-size: 10px; margin-top: 10px;">Date: _________________</div>
            </div>
            <div class="sig-box">
              <div style="font-size: 10px; text-transform: uppercase; color: #0a1628; font-weight: bold;">Holder / Participant Signature</div>
              <div class="sig-line"></div>
              <div style="font-size: 10px;">${user?.name || 'Accredited Investor'}</div>
              <div style="font-size: 10px; margin-top: 10px;">Date: _________________</div>
            </div>
          </div>
          
          <div class="footer">
            <div style="display: flex; justify-content: center; gap: 30px; margin-bottom: 10px;">
              <span>🔒 PRIVATE OBLIGATION</span>
              <span>⚖ TREASURY-INDEXED</span>
              <span>⚡ ETHEREUM MAINNET</span>
            </div>
            <p>Bethereum Private Wealth Management • Treasury-Indexed Private Obligations</p>
            <p style="font-family: monospace;">BETH Token: ${BETH_TOKEN}</p>
            <p>Generated: ${new Date().toLocaleString()}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="treasury-securities-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="text-[#b8860b] hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                DASHBOARD
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-white text-lg font-light tracking-wide flex items-center gap-2">
                <Landmark className="w-5 h-5 text-[#b8860b]" />
                Treasury-Indexed Private Obligations
              </h1>
              <p className="text-xs text-gray-500">Short-Term • Medium-Term • Long-Term Obligations with BETH Credit Enhancement</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {account && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Connected Wallet</p>
                <p className="text-sm text-white font-mono">{account.slice(0, 8)}...{account.slice(-6)}</p>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={fetchOrders}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Important Disclaimer Banner */}
      <div className="bg-gradient-to-r from-yellow-900/30 to-yellow-800/30 border-b border-yellow-700/50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center gap-3 text-yellow-200 text-xs">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p>
              <strong>IMPORTANT:</strong> These are private contractual obligations, not U.S. Government securities. 
              Treasury rates are used as a reference index only. No government affiliation, guarantee, or insurance.
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <Button
            variant={activeTab === 'products' ? 'default' : 'outline'}
            onClick={() => setActiveTab('products')}
            className={activeTab === 'products' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="products-tab"
          >
            <Landmark className="w-4 h-4 mr-2" />
            OBLIGATIONS
          </Button>
          <Button
            variant={activeTab === 'calculator' ? 'default' : 'outline'}
            onClick={() => setActiveTab('calculator')}
            className={activeTab === 'calculator' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="calculator-tab"
          >
            <Calculator className="w-4 h-4 mr-2" />
            COST CALCULATOR
          </Button>
          <Button
            variant={activeTab === 'fees' ? 'default' : 'outline'}
            onClick={() => setActiveTab('fees')}
            className={activeTab === 'fees' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="fees-tab"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            FEE SCHEDULE
          </Button>
          <Button
            variant={activeTab === 'orders' ? 'default' : 'outline'}
            onClick={() => setActiveTab('orders')}
            className={activeTab === 'orders' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="orders-tab"
          >
            <FileText className="w-4 h-4 mr-2" />
            MY OBLIGATIONS ({orders.length})
          </Button>
          <Button
            variant={activeTab === 'legal' ? 'default' : 'outline'}
            onClick={() => setActiveTab('legal')}
            className={activeTab === 'legal' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="legal-tab"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            LEGAL FRAMEWORK
          </Button>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="p-4 bg-[#0d1d35] border border-[#1a2d4a] rounded-lg">
              <h3 className="text-[#b8860b] text-sm font-medium mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Treasury-Indexed Private Obligations
              </h3>
              <p className="text-gray-400 text-sm">
                Private contractual obligations whose economic terms are indexed to publicly available U.S. Treasury 
                benchmark rates for reference purposes only. Available as Short-Term, Medium-Term, and Long-Term obligations 
                with BETH token credit enhancement.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="products-grid">
              {TIPO_PRODUCTS.map((product) => (
                <Card 
                  key={product.id} 
                  className={`bg-[#0d1d35] border-[#1a2d4a] hover:border-[#b8860b] transition-colors cursor-pointer ${selectedProduct?.id === product.id ? 'border-[#b8860b] border-2' : ''}`}
                  onClick={() => handleProductSelect(product)}
                  data-testid={`product-${product.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            product.category === 'Short-Term' ? 'bg-green-500/20 text-green-400' :
                            product.category === 'Medium-Term' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>
                            {product.category}
                          </span>
                        </div>
                        <CardTitle className="text-white text-base">{product.name}</CardTitle>
                        <CardDescription className="text-gray-400 mt-1 text-xs">{product.description}</CardDescription>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-[#b8860b]">{product.baseYield}%</div>
                        <div className="text-xs text-gray-500">Indexed Yield</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-3 bg-[#0a1628] rounded">
                        <Calendar className="w-4 h-4 mx-auto text-[#b8860b] mb-1" />
                        <div className="text-white font-bold">{product.term}</div>
                        <div className="text-xs text-gray-500">Days</div>
                      </div>
                      <div className="text-center p-3 bg-[#0a1628] rounded">
                        <DollarSign className="w-4 h-4 mx-auto text-[#b8860b] mb-1" />
                        <div className="text-white font-bold text-sm">{formatCurrency(product.minInvestment)}</div>
                        <div className="text-xs text-gray-500">Min</div>
                      </div>
                      <div className="text-center p-3 bg-[#0a1628] rounded">
                        <Landmark className="w-4 h-4 mx-auto text-[#b8860b] mb-1" />
                        <div className="text-white font-bold text-xs">{product.referenceRate.replace('U.S. Treasury ', '').replace(' CMR', '')}</div>
                        <div className="text-xs text-gray-500">Index</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {product.features.map((feature, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-[#1a2d4a] text-gray-300 rounded">
                          <CheckCircle className="w-3 h-3 inline mr-1 text-green-400" />
                          {feature}
                        </span>
                      ))}
                    </div>
                    <Button 
                      className="w-full mt-4 bg-[#b8860b] hover:bg-[#9a7209]"
                      onClick={(e) => { e.stopPropagation(); handleProductSelect(product); setActiveTab('calculator'); }}
                      data-testid={`select-${product.id}`}
                    >
                      Calculate Terms <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[#0d1d35] border-[#1a2d4a]" data-testid="calculator-input">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-[#b8860b]" />
                  Obligation Calculator
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Select an obligation type and enter principal amount
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Select Obligation</label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {TIPO_PRODUCTS.map((product) => (
                      <Button
                        key={product.id}
                        variant={selectedProduct?.id === product.id ? 'default' : 'outline'}
                        className={selectedProduct?.id === product.id ? 'bg-[#b8860b]' : 'border-[#1a2d4a] text-gray-400'}
                        onClick={() => handleProductSelect(product)}
                        size="sm"
                      >
                        {product.term}D @ {product.baseYield}%
                      </Button>
                    ))}
                  </div>
                </div>
                
                {selectedProduct && (
                  <>
                    <div>
                      <label className="text-xs text-gray-400 uppercase tracking-wider">Principal Amount (USD)</label>
                      <Input
                        type="number"
                        value={investmentAmount}
                        onChange={(e) => handleAmountChange(e.target.value)}
                        placeholder="Enter amount"
                        className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                        min={selectedProduct.minInvestment}
                        max={selectedProduct.maxInvestment}
                        data-testid="investment-amount"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Min: {formatCurrency(selectedProduct.minInvestment)} | Max: {formatCurrency(selectedProduct.maxInvestment)}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-[#0a1628] rounded-lg">
                      <h4 className="text-sm text-[#b8860b] mb-3">Selected Obligation</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Type</span>
                          <span className="text-white">{selectedProduct.category}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Reference Index</span>
                          <span className="text-white">{selectedProduct.referenceRate}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Spread</span>
                          <span className="text-white">+{selectedProduct.spread}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Indexed Yield</span>
                          <span className="text-[#b8860b]">{selectedProduct.baseYield}% APY</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#0d1d35] border-[#1a2d4a]" data-testid="cost-sheet">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#b8860b]" />
                  Cost Sheet
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Detailed terms and platform fees
                </CardDescription>
              </CardHeader>
              <CardContent>
                {costSheet ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-lg border border-[#b8860b]/30">
                      <div className="text-xs text-[#b8860b] uppercase tracking-wider mb-2">Maturity Value</div>
                      <div className="text-3xl font-bold text-white">${parseFloat(costSheet.maturityValue).toLocaleString()}</div>
                      <div className="text-sm text-green-400 mt-1">+${parseFloat(costSheet.netAccrual).toLocaleString()} net ({costSheet.effectiveYield}% effective)</div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Principal</span>
                        <span className="text-white">${parseFloat(costSheet.principal).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Gross Accrual ({costSheet.term}d)</span>
                        <span className="text-green-400">+${parseFloat(costSheet.grossAccrual).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                      <div className="text-xs text-blue-400 mb-2 font-medium">PLATFORM FEES (Fixed, Non-Performance)</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Issuance Fee</span>
                          <span className="text-red-400">-${parseFloat(costSheet.issuanceFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Admin Fee</span>
                          <span className="text-red-400">-${parseFloat(costSheet.adminFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Enhancement Fee</span>
                          <span className="text-red-400">-${parseFloat(costSheet.enhancementFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-blue-700/50">
                          <span className="text-gray-400">Total Fees</span>
                          <span className="text-red-400">-${parseFloat(costSheet.totalFees).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-[#0a1628] rounded-lg">
                      <div className="flex items-center gap-2 text-xs text-[#b8860b] mb-2">
                        <Lock className="w-4 h-4" />
                        CREDIT ENHANCEMENT (BETH)
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">BETH Required</span>
                        <span className="text-white">{parseFloat(costSheet.bethCollateral).toLocaleString()} BETH</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-400">Collateralization</span>
                        <span className="text-white">{costSheet.collateralRatio}%</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                        onClick={handleCreateOrder}
                        disabled={loading || !token}
                        data-testid="create-order-btn"
                      >
                        {loading ? 'Creating...' : 'Create Obligation'}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-[#1a2d4a] text-[#b8860b]"
                        onClick={() => printTIPODocument(null)}
                        data-testid="print-tipo-btn"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Select an obligation and enter amount to see terms</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Fee Schedule Tab */}
        {activeTab === 'fees' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-white text-xl font-light">Platform Fee Schedule</h2>
                <p className="text-gray-500 text-sm">Fixed, non-performance based fees</p>
              </div>
              <Button onClick={printFeeSchedule} className="bg-[#b8860b] hover:bg-[#9a7209]" data-testid="print-fee-schedule">
                <Printer className="w-4 h-4 mr-2" /> Print Fee Schedule
              </Button>
            </div>
            
            {/* Fee Disclosure */}
            <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-yellow-200 text-sm">
                  <strong>FEE DISCLOSURE:</strong> Fees are charged for platform access, administrative services, documentation, 
                  and credit-enhancement availability. <strong>No fees are contingent upon investment success, performance, or capital formation.</strong>
                </p>
              </div>
            </div>
            
            {/* Fee Categories */}
            {Object.entries(FEE_STRUCTURE).map(([key, category]) => (
              <Card key={key} className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    {key === 'platformAccess' && <Settings className="w-5 h-5 text-[#b8860b]" />}
                    {key === 'documentation' && <FileText className="w-5 h-5 text-[#b8860b]" />}
                    {key === 'administration' && <Layers className="w-5 h-5 text-[#b8860b]" />}
                    {key === 'creditEnhancement' && <Shield className="w-5 h-5 text-[#b8860b]" />}
                    {key === 'technology' && <Calculator className="w-5 h-5 text-[#b8860b]" />}
                    {category.title}
                    {category.safe && (
                      <span className="ml-2 text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">✓ SAFE</span>
                    )}
                  </CardTitle>
                  <CardDescription className="text-gray-400">{category.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  {category.tiers ? (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {category.tiers.map((tier, idx) => (
                        <div key={idx} className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                          <h4 className="text-white font-medium text-sm">{tier.name}</h4>
                          <div className="text-2xl font-bold text-[#b8860b] my-2">${tier.price.toLocaleString()}</div>
                          <p className="text-xs text-gray-500 mb-3">{tier.period}</p>
                          <ul className="space-y-1">
                            {tier.features.map((f, i) => (
                              <li key={i} className="text-xs text-gray-400 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-400" /> {f}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {category.fees.map((fee, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-[#0a1628] rounded">
                          <span className="text-gray-300 text-sm">{fee.name}</span>
                          <div className="text-right">
                            <span className="text-[#b8860b] font-bold">${fee.amount.toLocaleString()}</span>
                            <span className="text-xs text-gray-500 block">{fee.type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {category.reason && (
                    <p className="text-xs text-gray-500 mt-4 italic">Why it's safe: {category.reason}</p>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {/* Payment Stack */}
            <Card className="bg-gradient-to-r from-[#0d1d35] to-[#1a2d4a] border-[#b8860b]/30">
              <CardHeader>
                <CardTitle className="text-[#b8860b]">💰 Payment Stack</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center space-y-4">
                  <div className="p-3 bg-[#0a1628] rounded-lg text-white text-center w-full max-w-md">User / Trust / SPV</div>
                  <ChevronRight className="w-6 h-6 text-[#b8860b] rotate-90" />
                  <div className="p-3 bg-[#0a1628] rounded-lg text-white text-center w-full max-w-md">Platform Access Fee (monthly)</div>
                  <ChevronRight className="w-6 h-6 text-[#b8860b] rotate-90" />
                  <div className="p-3 bg-[#0a1628] rounded-lg text-white text-center w-full max-w-md">Issuance Fee (one-time)</div>
                  <ChevronRight className="w-6 h-6 text-[#b8860b] rotate-90" />
                  <div className="p-3 bg-[#0a1628] rounded-lg text-white text-center w-full max-w-md">Admin / Servicing Fee (fixed)</div>
                  <ChevronRight className="w-6 h-6 text-[#b8860b] rotate-90" />
                  <div className="p-3 bg-[#0a1628] rounded-lg text-white text-center w-full max-w-md">Credit Enhancement Facility Fee (fixed)</div>
                </div>
              </CardContent>
            </Card>
            
            {/* Platform Does Not */}
            <Card className="bg-red-900/20 border-red-700/50">
              <CardHeader>
                <CardTitle className="text-red-400">🚫 Platform Does NOT</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    'Take % of obligation value',
                    'Take % of Treasury spread',
                    'Take success fees',
                    'Take referral fees',
                    'Share in investor returns',
                    'Net from settlement flows'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-red-300 text-sm">
                      <span className="text-red-400">✗</span> {item}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Platform Positioning */}
            <Card className="bg-blue-900/20 border-blue-700/50">
              <CardHeader>
                <CardTitle className="text-blue-400">Platform Positioning</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-200 text-sm mb-4">
                  Bethereum operates as:
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-blue-800/50 text-blue-300 rounded">A Platform</span>
                  <span className="px-3 py-1 bg-blue-800/50 text-blue-300 rounded">A Technology Provider</span>
                  <span className="px-3 py-1 bg-blue-800/50 text-blue-300 rounded">A Document Issuer</span>
                  <span className="px-3 py-1 bg-blue-800/50 text-blue-300 rounded">A Credit-Enhancement Facility Administrator</span>
                </div>
                <p className="text-gray-400 text-sm">
                  <strong className="text-red-400">NOT</strong> as a broker. 
                  <strong className="text-red-400"> NOT</strong> as a dealer. 
                  <strong className="text-red-400"> NOT</strong> as a yield participant (unless via a separate SPV).
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]" data-testid="orders-list">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#b8860b]" />
                My Treasury-Indexed Obligations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length > 0 ? (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.order_id} className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]" data-testid={`order-${order.order_id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-white font-medium">{order.product_name}</h4>
                          <p className="text-xs text-gray-500 font-mono">{order.order_id}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          order.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          order.status === 'matured' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {order.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Principal</span>
                          <p className="text-white">${parseFloat(order.principal || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Indexed Yield</span>
                          <p className="text-[#b8860b]">{order.base_yield}%</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Term</span>
                          <p className="text-white">{order.term_days} Days</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Created</span>
                          <p className="text-white">{new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button variant="outline" size="sm" className="border-[#1a2d4a] text-[#b8860b]" onClick={() => printTIPODocument(order)}>
                          <Printer className="w-4 h-4 mr-1" /> Print Document
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No obligations yet. Create your first obligation.</p>
                  <Button className="mt-4 bg-[#b8860b] hover:bg-[#9a7209]" onClick={() => setActiveTab('products')}>
                    Browse Obligations
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Legal Framework Tab - Updated without Acceptable/DO NOT Use sections */}
        {activeTab === 'legal' && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]" data-testid="legal-framework">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Scale className="w-5 h-5 text-[#b8860b]" />
                Legal Framework: Treasury-Indexed Private Obligations
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-invert max-w-none">
              <div className="space-y-6 text-gray-300 text-sm">
                <div className="p-4 bg-[#0a1628] rounded-lg border-l-4 border-[#b8860b]">
                  <h3 className="text-[#b8860b] text-lg font-medium mb-2">Instrument Structure</h3>
                  <p className="mb-2"><strong>Entity A ↔ Entity B</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li><strong>Reference:</strong> U.S. Treasury Index</li>
                    <li><strong>Settlement:</strong> Private obligation</li>
                    <li><strong>Support:</strong> Credit enhancement</li>
                    <li><strong>Payment:</strong> Tokenized instruction</li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-white text-lg font-medium border-b border-[#1a2d4a] pb-2">Key Legal Provisions</h3>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">1. Instrument Name</h4>
                    <p className="text-gray-400">Treasury-Indexed Participation Obligation (TIPO) — also acceptable: Treasury-Linked Private Note. This is a private contractual obligation, not a security issued by the U.S. Government.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">2. Parties</h4>
                    <ul className="list-disc list-inside text-gray-400 space-y-1">
                      <li><strong>Issuer / Obligor:</strong> [Your SPV or Platform Treasury Entity]</li>
                      <li><strong>Holder / Participant:</strong> Accredited Investor, Trust, or SPV</li>
                      <li><strong>Platform Administrator:</strong> Bethereum (Administrative only; non-fiduciary)</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">3. Purpose</h4>
                    <p className="text-gray-400">The purpose of this Obligation is to evidence a private payment obligation whose economic terms are indexed to a publicly available U.S. Treasury benchmark for reference purposes only.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">4. Reference Index (Critical Language)</h4>
                    <p className="text-gray-400">Payments under this Obligation shall be indexed to the U.S. Treasury [X-Year] Constant Maturity Rate, as published by the Federal Reserve Economic Data (FRED), solely as a reference benchmark.</p>
                    <div className="mt-2 p-3 bg-yellow-900/20 border border-yellow-700/50 rounded text-yellow-200 text-xs">
                      ⚠️ No ownership, custody, or interest in U.S. Treasuries is created.
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">5. Principal & Term</h4>
                    <ul className="list-disc list-inside text-gray-400 space-y-1">
                      <li>Principal Amount: $________</li>
                      <li>Issue Date: ________</li>
                      <li>Maturity Date: ________</li>
                      <li>Options: Bullet maturity, Periodic settlement, Roll-forward election</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">6. Credit Enhancement (BETH-Backed)</h4>
                    <p className="text-gray-400">Performance of this Obligation may be supported by a separate Credit Enhancement and Participation Agreement (PAS), which may reference digital assets solely as a performance support mechanism.</p>
                    <ul className="list-disc list-inside text-gray-500 mt-2 space-y-1 text-xs">
                      <li>Enhancement ≠ guarantee</li>
                      <li>Enhancement ≠ legal tender</li>
                      <li>Enhancement does not eliminate credit risk</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">7. Settlement Mechanics (Tokenized Check)</h4>
                    <p className="text-gray-400">Settlement of amounts due may be executed via a tokenized settlement instruction, functioning as a conditional payment directive subject to off-chain accounting confirmation.</p>
                    <div className="mt-2 p-3 bg-[#0a1628] rounded text-gray-500 text-xs">
                      📌 Tokenized check: Is not money • Does not constitute legal tender • Does not effect payment absent acceptance
                    </div>
                  </div>
                  
                  <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
                    <h4 className="text-red-400 font-medium">8. NO GOVERNMENT AFFILIATION (MANDATORY)</h4>
                    <p className="text-red-300 text-sm mt-2">This Obligation is NOT issued, guaranteed, sponsored, or insured by the United States Treasury, the Federal Reserve, or any governmental authority.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">9. Transfer Restrictions</h4>
                    <ul className="list-disc list-inside text-gray-400 space-y-1">
                      <li>No public resale</li>
                      <li>No exchange listing</li>
                      <li>Transfers only to: Accredited entities, With issuer consent, Ledger update required</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">10. Platform Role Limitation</h4>
                    <p className="text-gray-400">The Platform acts solely as a technology and administrative service provider and does not provide investment advice, brokerage services, or fiduciary oversight.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">11. Governing Law</h4>
                    <p className="text-gray-400">State of Delaware, USA. Private commercial contract law. UCC Articles 8 & 9 (as applicable).</p>
                  </div>
                  
                  <div>
                    <h4 className="text-[#b8860b] font-medium">12. Default & Remedies (Private-Law Only)</h4>
                    <p className="text-gray-400">Payment failure, Credit enhancement draw (if applicable), Netting, Offset, Restructuring or roll-forward.</p>
                    <p className="text-red-400 text-sm mt-2"><strong>❌ No unilateral debt discharge. ❌ No consumer debt application.</strong></p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Client Details Modal */}
        {showClientDetailsModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="client-details-modal">
            <Card className="max-w-2xl w-full bg-[#0d1d35] border-[#1a2d4a] my-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#b8860b]" />
                  Client Information
                </CardTitle>
                <CardDescription>
                  Please provide your details to complete the TIPO order
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Full Legal Name / Entity Name</label>
                    <Input
                      value={clientDetails.client_name}
                      onChange={(e) => setClientDetails(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="Full Name or Entity Name"
                      className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                      data-testid="client-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Entity Type</label>
                    <select
                      value={clientDetails.client_entity_type}
                      onChange={(e) => setClientDetails(prev => ({ ...prev, client_entity_type: e.target.value }))}
                      className="mt-1 w-full bg-[#0a1628] border border-[#1a2d4a] text-white rounded-md p-2"
                    >
                      <option value="individual">Individual</option>
                      <option value="trust">Trust</option>
                      <option value="llc">LLC</option>
                      <option value="corporation">Corporation</option>
                      <option value="partnership">Partnership</option>
                      <option value="spv">SPV / Special Purpose Vehicle</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Tax ID / EIN / SSN</label>
                    <Input
                      value={clientDetails.client_tax_id}
                      onChange={(e) => setClientDetails(prev => ({ ...prev, client_tax_id: e.target.value }))}
                      placeholder="XX-XXXXXXX"
                      className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Street Address</label>
                    <Input
                      value={clientDetails.client_address}
                      onChange={(e) => setClientDetails(prev => ({ ...prev, client_address: e.target.value }))}
                      placeholder="Street Address"
                      className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                      data-testid="client-address-input"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">City</label>
                    <Input
                      value={clientDetails.client_city}
                      onChange={(e) => setClientDetails(prev => ({ ...prev, client_city: e.target.value }))}
                      placeholder="City"
                      className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-400 uppercase tracking-wider">State</label>
                      <Input
                        value={clientDetails.client_state}
                        onChange={(e) => setClientDetails(prev => ({ ...prev, client_state: e.target.value.toUpperCase() }))}
                        placeholder="ST"
                        maxLength={2}
                        className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 uppercase tracking-wider">ZIP</label>
                      <Input
                        value={clientDetails.client_zip}
                        onChange={(e) => setClientDetails(prev => ({ ...prev, client_zip: e.target.value }))}
                        placeholder="ZIP"
                        className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Email</label>
                    <Input
                      type="email"
                      value={clientDetails.client_email}
                      onChange={(e) => setClientDetails(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="email@example.com"
                      className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Phone</label>
                    <Input
                      value={clientDetails.client_phone}
                      onChange={(e) => setClientDetails(prev => ({ ...prev, client_phone: e.target.value }))}
                      placeholder="(XXX) XXX-XXXX"
                      className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 justify-end pt-4 border-t border-[#1a2d4a]">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowClientDetailsModal(false)} 
                    className="border-[#1a2d4a]"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => {
                      if (!clientDetails.client_name || !clientDetails.client_address) {
                        toast.error('Please fill in required fields (Name and Address)');
                        return;
                      }
                      setShowClientDetailsModal(false);
                      handleCreateOrder();
                    }}
                    disabled={loading}
                    className="bg-[#b8860b] hover:bg-[#9a7209]"
                    data-testid="confirm-order-btn"
                  >
                    {loading ? 'Processing...' : 'Confirm & Create Order'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default TreasurySecurities;
